import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import DramaCard from "@/components/base/DramaCard";
import { Breadcrumbs, EmptyState } from "@/components/feature/PageShell";
import { findDrama } from "@/mocks/dramas";
import { findSpecial, specials } from "@/mocks/specials";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialPage() {
  const { id = "" } = useParams();
  const special = findSpecial(id);

  usePageMeta({
    title: special ? `${special.title} - 推荐专题 - 日本伦理剧` : "专题未找到 - 日本伦理剧",
    description: special
      ? `${special.lead}本专题收录 ${special.editorPickIds.length} 部日本伦理题材作品，附编辑推荐理由与观看顺序建议。`
      : "该专题可能已下线，欢迎浏览其他策划专题。",
    keywords: special ? `${special.title},日本伦理剧专题,${special.kicker}` : "日本伦理剧专题",
  });

  if (!special) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page py-20">
          <EmptyState
            icon="ri-article-line"
            title="这个专题不存在"
            desc="专题可能已归档，或者链接输入有误。你可以从下方的专题列表中重新选择。"
            action={
              <Link
                to="/special/family-web"
                className="rounded-md bg-primary-900 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-800"
              >
                浏览热门专题
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const picks = special.editorPickIds
    .map((dramaId) => findDrama(dramaId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const related = special.relatedIds
    .map((specialId) => findSpecial(specialId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={special.cover}
              alt={`${special.title} 专题主图`}
              title={`${special.title} 日本伦理剧专题`}
              className="h-full w-full object-cover object-top opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/40" />
          </div>
          <div className="container-page relative py-12 md:py-20">
            <div className="[&_a]:text-primary-200 [&_span]:text-primary-300">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "推荐专题", to: "/special/family-web" },
                  { label: special.kicker },
                ]}
              />
            </div>
            <p className="mt-8 font-label text-[11px] tracking-[0.24em] text-accent-300">{special.kicker.toUpperCase()}</p>
            <h1 className="mt-4 max-w-3xl font-heading text-[28px] font-semibold leading-tight text-background-50 md:text-[44px]">
              {special.title}
            </h1>
            <p className="mt-4 max-w-2xl font-heading text-sm leading-relaxed text-accent-200 md:text-base">
              「{special.lead}」
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-primary-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-film-line" /> 收录 {picks.length} 部
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-edit-2-line" /> 编辑部策划
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-book-open-line" /> 含 {special.reviews.length} 篇点评
              </span>
            </div>
          </div>
        </section>

        <section className="container-page py-10 md:py-14">
          <div className="grid gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">导语</h2>
                </div>
                <p className="mt-4 text-[15px] leading-loose text-foreground-800">{special.intro}</p>
              </div>

              <div className="mt-8">
                <div className="flex items-end justify-between">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">策划片单</h2>
                  <span className="text-[11px] text-foreground-400">按推荐观看顺序排列</span>
                </div>
                <div className="mt-4 space-y-3">
                  {picks.map((drama, index) => (
                    <Link
                      key={drama.id}
                      to={`/detail/${drama.id}`}
                      className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300 md:gap-5 md:p-4"
                    >
                      <span className="w-6 shrink-0 font-heading text-lg font-semibold text-background-400 md:text-xl">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="h-[104px] w-[76px] shrink-0 overflow-hidden rounded bg-background-200 md:h-[120px] md:w-[88px]">
                        <img
                          src={drama.cover}
                          alt={`${drama.title} 封面`}
                          title={`${drama.title} 日本伦理剧`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-800">
                          {drama.title}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">{drama.tagline}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                          <span>{drama.year}</span>
                          <span>{drama.types[0]}</span>
                          <span>{drama.episodes} 集</span>
                          <span className="text-accent-700">{drama.rating.toFixed(1)} 分</span>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line hidden shrink-0 text-foreground-400 md:block" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h2 className="font-heading text-xl font-semibold text-foreground-950">推荐理由与编辑点评</h2>
                <div className="mt-4 space-y-4">
                  {special.reviews.map((review, index) => (
                    <div key={review.title} className="rounded-lg border border-background-200 bg-accent-50/60 p-5">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 font-label text-[11px] font-semibold text-background-50">
                          {index + 1}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-950">{review.title}</h3>
                      </div>
                      <p className="mt-3 text-sm leading-loose text-foreground-700">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <p className="rule-label text-foreground-500">相关专题</p>
                  <div className="mt-3 space-y-3">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/special/${item.id}`}
                        className="group flex gap-3 rounded-md border border-background-200 p-2.5 transition-colors hover:border-accent-300"
                      >
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 专题主图`}
                            title={item.title}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] text-accent-700">{item.kicker}</p>
                          <p className="mt-0.5 line-clamp-2 text-xs font-medium text-foreground-900 group-hover:text-primary-800">
                            {item.title}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                  <p className="rule-label text-secondary-800">全部专题</p>
                  <ul className="mt-3 divide-y divide-secondary-200">
                    {specials.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/special/${item.id}`}
                          className={`flex items-center justify-between gap-2 py-2.5 text-xs transition-colors hover:text-accent-700 ${
                            item.id === special.id ? "font-medium text-primary-900" : "text-foreground-700"
                          }`}
                        >
                          <span className="line-clamp-1">{item.title}</span>
                          <i className="ri-arrow-right-s-line shrink-0 text-foreground-400" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-12 border-t border-background-200 pt-8">
            <div className="flex items-end justify-between">
              <h2 className="font-heading text-xl font-semibold text-foreground-950">其他精选作品</h2>
              <Link to="/category/ethics-drama" className="text-xs text-foreground-500 hover:text-accent-700">
                查看全部分类 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
              {picks.slice(0, 4).map((drama) => (
                <DramaCard key={`bottom-${drama.id}`} drama={drama} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}